/*
 * Add-on cart reconciliation (site-wide).
 *
 * Priced product add-ons are added to the cart as their own line items (tagged product,
 * hidden from the cart display) and linked to their parent product line by a shared
 * `_bundle_id` property; the add-on lines also carry `_addon: 'true'`.
 *
 * Because those lines are hidden, the customer can never act on them directly, so anything
 * they do to the parent line has to be mirrored onto them here. Two rules, both keyed off
 * `_bundle_id`:
 *
 * 1. ORPHANS - the parent line is gone, so the add-on lines are removed. Without this a
 *    customer who deletes the parent keeps paying for invisible lines.
 * 2. QUANTITY DRIFT - the parent's quantity changed, so the add-on lines are set to match.
 *    assets/main-product-addons.js only ties the two together at add-to-cart time (it posts
 *    each add-on with `quantity: mainQuantity`). After that they are independent line items,
 *    and the cart page / mini-cart quantity controls call /cart/change.js for the parent's
 *    line index alone - the hidden add-on lines kept whatever quantity they were added with.
 *    A parent raised from 1 to 3 therefore shipped 3 products with 1 add-on, and the parent
 *    row's displayed total (which folds in the add-on lines - see the `line_price_html` block
 *    in sections/main-cart-items.liquid) under-stated the real charge.
 *
 * Runs everywhere the cart can change (the mini-cart drawer is site-wide, plus the cart page).
 */
(function () {
  if (window.__addonCartCleanupInit) return;
  window.__addonCartCleanupInit = true;

  var cleaning = false;
  // A cart event that lands mid-flight is not dropped: the pass in progress re-runs once when
  // it finishes, so rapid quantity clicks cannot leave the add-on lines behind.
  var rerun = false;
  // { lineItemKey: quantity } we asked for and did NOT get — an add-on whose own stock is lower
  // than the parent's quantity, which Shopify clamps. Without this the mismatch is permanent,
  // so every later cart change anywhere would fire another pointless update and re-render.
  // The entry is ignored again as soon as the parent moves to a different quantity.
  var clamped = {};

  function cartBaseUrl() {
    return (window.routes && window.routes.cart_url) || "/cart";
  }

  function cartUpdateUrl() {
    return (window.routes && window.routes.cart_update_url) || "/cart/update.js";
  }

  // Every add-on line that is out of step with its parent, as a { lineItemKey: quantity } map
  // for /cart/update.js — 0 removes an orphan, otherwise the parent's own quantity. Returns
  // null when the cart is already consistent.
  //
  // Nothing here can loop: the re-render this file performs afterwards (updateSectionContents
  // on the cart page, renderContents in the drawer) publishes no cart event, so a pass is never
  // re-entered by its own work. Should some app fire one anyway, the next pass sees a
  // consistent cart and returns null.
  //
  // Keyed by line item key rather than line index, so it is unaffected by add-on lines being
  // skipped in the rendered cart and by indexes shifting as lines are removed.
  function bundleUpdates(cart) {
    var parentQuantities = {};
    cart.items.forEach(function (item) {
      var props = item.properties || {};
      if (props._bundle_id && props._addon !== "true") {
        parentQuantities[props._bundle_id] = item.quantity;
      }
    });

    var updates = {};
    var hasOrphans = false;
    var found = false;
    cart.items.forEach(function (item) {
      var props = item.properties || {};
      if (props._addon !== "true" || !props._bundle_id) return;

      var parentQuantity = parentQuantities[props._bundle_id];
      if (parentQuantity === undefined) {
        // Parent is gone.
        updates[item.key] = 0;
        hasOrphans = true;
        found = true;
      } else if (item.quantity !== parentQuantity && clamped[item.key] !== parentQuantity) {
        // Parent's quantity moved; follow it.
        updates[item.key] = parentQuantity;
        found = true;
      }
    });

    return found ? { updates: updates, hasOrphans: hasOrphans } : null;
  }

  function cleanup() {
    if (cleaning) {
      rerun = true;
      return;
    }
    cleaning = true;

    fetch(cartBaseUrl() + ".js", { headers: { Accept: "application/json" } })
      .then(function (response) {
        return response.json();
      })
      .then(function (cart) {
        var result = bundleUpdates(cart);
        if (!result) return null;

        var minicart = document.querySelector("mini-cart");
        var body = { updates: result.updates };
        if (minicart) {
          body.sections = minicart.getSectionsToRender().map(function (section) {
            return section.section;
          });
          body.sections_url = window.location.pathname;
        }

        return fetch(cartUpdateUrl(), {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Accept: "application/javascript",
            "X-Requested-With": "XMLHttpRequest",
          },
          body: JSON.stringify(body),
        })
          .then(function (response) {
            return response.json();
          })
          .then(function (payload) {
            // Record anything Shopify would not give us (add-on stock below the parent's
            // quantity) so the next pass does not keep asking for the same impossible number.
            if (payload && payload.items) {
              payload.items.forEach(function (item) {
                var wanted = result.updates[item.key];
                if (wanted === undefined || wanted === 0) return;
                if (item.quantity !== wanted) {
                  clamped[item.key] = wanted;
                } else {
                  delete clamped[item.key];
                }
              });
            }

            var onCartPage = !!document.getElementById("main-cart-items");

            // Orphan removal deletes whole rows and can empty the cart, which needs more than
            // the row markup swapped out. Keep the full reload this file has always done.
            if (result.hasOrphans && onCartPage) {
              window.location.reload();
              return;
            }

            // A pure quantity sync only touches hidden lines, so re-render in place - the
            // parent row's folded total and the cart footer both need refreshing, but
            // reloading the page on every quantity click would be unusable. cart-items owns
            // the list of sections to refresh, so reuse its own method rather than
            // duplicating it here.
            if (onCartPage) {
              var cartItems = document.querySelector("cart-items");
              if (cartItems && cartItems.updateSectionContents) {
                return cartItems.updateSectionContents();
              }
              window.location.reload();
              return;
            }

            if (minicart && payload.sections) minicart.renderContents(payload);
          });
      })
      .catch(function (error) {
        console.error(error);
      })
      .finally(function () {
        cleaning = false;
        if (rerun) {
          rerun = false;
          cleanup();
        }
      });
  }

  function init() {
    if (typeof subscribe !== "function" || typeof PUB_SUB_EVENTS === "undefined") return;
    subscribe(PUB_SUB_EVENTS.cartItemRemove, cleanup);
    subscribe(PUB_SUB_EVENTS.cartUpdate, cleanup);
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }
})();
